import {Box, useTheme} from '@mui/material';
import {useMemo, useRef, useEffect, useState} from 'react';
import type {
  EquationLine,
  ScatterAxis,
  ScatterData,
  ScatterDataSet,
  ScatterDataPoint,
  ScatterLegend,
  ScatterLine,
  SvgPathOverlay,
} from '../../utils/pebbleplotScatterSchema';
import {buildSvgPathDFromPoints, collectOverlayPoints, getFirstMoveToPoint} from './scatterOverlays';

interface ScatterPlotGraphProps {
  data?: ScatterData;
  xAxis?: ScatterAxis;
  yAxis?: ScatterAxis;
  y2Axis?: ScatterAxis;
  legend?: ScatterLegend;
  line?: ScatterLine;
  equationLines?: EquationLine[];
  svgPaths?: SvgPathOverlay[];
  width?: number;
  height?: number;
}

export default function ScatterPlotGraph({
  data,
  xAxis,
  yAxis,
  y2Axis,
  legend,
  line,
  equationLines,
  svgPaths,
  width,
  height,
}: ScatterPlotGraphProps) {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);

  // Helper to resolve color (theme color or CSS color)
  const resolveColor = (color: string): string => {
    if (color.startsWith('#') || color.startsWith('rgb')) {
      return color;
    }
    // Try to resolve as theme color
    const colorParts = color.split('.');
    if (colorParts.length === 2) {
      const [paletteKey, colorKey] = colorParts;
      const palette = theme.palette[paletteKey as keyof typeof theme.palette];
      if (palette && typeof palette === 'object' && colorKey in palette) {
        return (palette as any)[colorKey];
      }
    }
    return color;
  };

  const dataSets = useMemo<ScatterDataSet[]>(() => {
    if (!data) return [];
    if (!Array.isArray(data) || data.length === 0) return [];
    const first = data[0] as unknown;
    const isLegacyPoints =
      typeof first === 'object' &&
      first !== null &&
      'x' in (first as Record<string, unknown>) &&
      'y' in (first as Record<string, unknown>);

    if (isLegacyPoints) {
      return [{label: undefined, isY2Axis: false, data: data as ScatterDataPoint[]}];
    }

    return data as ScatterDataSet[];
  }, [data]);

  const allPoints = useMemo(() => dataSets.flatMap((ds) => ds.data), [dataSets]);
  const yPoints = useMemo(() => dataSets.filter((ds) => !ds.isY2Axis).flatMap((ds) => ds.data), [dataSets]);
  const y2Points = useMemo(() => dataSets.filter((ds) => ds.isY2Axis).flatMap((ds) => ds.data), [dataSets]);
  const hasY2Axis = useMemo(() => Boolean(y2Axis) || dataSets.some((ds) => Boolean(ds.isY2Axis)), [dataSets, y2Axis]);

  const seriesPalette = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ],
    [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ],
  );
  const overlays = useMemo(() => {
    return collectOverlayPoints({
      data: yPoints.length > 0 ? yPoints : allPoints,
      equationLines,
      svgPaths,
      context: {xAxis, yAxis},
    });
  }, [allPoints, equationLines, svgPaths, xAxis, yAxis, yPoints]);

  // Calculate axis ranges
  const xValuesRaw = allPoints.map((point) => point.x).concat(overlays.equationPoints.map((p) => p.x));
  const yValuesRaw = (yPoints.length > 0 ? yPoints : allPoints)
    .map((point) => point.y)
    .concat(overlays.equationPoints.map((p) => p.y));
  const y2ValuesRaw = y2Points.map((point) => point.y);
  const xValues = xValuesRaw.filter((v) => Number.isFinite(v));
  const yValues = yValuesRaw.filter((v) => Number.isFinite(v));
  const y2Values = y2ValuesRaw.filter((v) => Number.isFinite(v));

  // Get crossAt values (default to 0 for both axes)
  const xAxisCrossAt = xAxis?.crossAt ?? 0;
  const xAxisCrossAt2 = xAxis?.crossAt2;
  const yAxisCrossAt = yAxis?.crossAt ?? 0;

  // Determine if min/max are explicitly set
  const xMinExplicit = xAxis?.min !== undefined;
  const xMaxExplicit = xAxis?.max !== undefined;
  const yMinExplicit = yAxis?.min !== undefined;
  const yMaxExplicit = yAxis?.max !== undefined;
  const y2MinExplicit = y2Axis?.min !== undefined;
  const y2MaxExplicit = y2Axis?.max !== undefined;

  // Calculate base min/max values
  const hasAnyValues = xValues.length > 0 && yValues.length > 0;
  const xDataMin = hasAnyValues ? Math.min(...xValues) : 0;
  const xDataMax = hasAnyValues ? Math.max(...xValues) : 1;
  const yDataMin = hasAnyValues ? Math.min(...yValues) : 0;
  const yDataMax = hasAnyValues ? Math.max(...yValues) : 1;
  const hasAnyY2Values = xValues.length > 0 && y2Values.length > 0;
  const y2DataMin = hasAnyY2Values ? Math.min(...y2Values) : 0;
  const y2DataMax = hasAnyY2Values ? Math.max(...y2Values) : 1;

  // Use explicit min/max if provided, otherwise calculate from data
  let xMin = xAxis?.min ?? xDataMin;
  let xMax = xAxis?.max ?? xDataMax;
  let yMin = yAxis?.min ?? yDataMin;
  let yMax = yAxis?.max ?? yDataMax;
  let y2Min = y2Axis?.min ?? y2DataMin;
  let y2Max = y2Axis?.max ?? y2DataMax;

  // Calculate ranges for tick step calculation (before adding padding)
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;

  // Add padding only if min/max are not explicitly set
  if (!xMinExplicit || !xMaxExplicit) {
    const xPadding = xRange * 0.1 || 0.1;
    if (!xMinExplicit) xMin = xMin - xPadding;
    if (!xMaxExplicit) xMax = xMax + xPadding;
  }

  if (!yMinExplicit || !yMaxExplicit) {
    const yPadding = yRange * 0.1 || 0.1;
    if (!yMinExplicit) yMin = yMin - yPadding;
    if (!yMaxExplicit) yMax = yMax + yPadding;
  }

  if (hasY2Axis && (!y2MinExplicit || !y2MaxExplicit)) {
    const y2Range = y2Max - y2Min || 1;
    const y2Padding = y2Range * 0.1 || 0.1;
    if (!y2MinExplicit) y2Min = y2Min - y2Padding;
    if (!y2MaxExplicit) y2Max = y2Max + y2Padding;
  }

  // Ensure domains include the crossAt values (only if they fall outside the range)
  // Note: when dual-axis is enabled, we default to "Excel-style" axes (bottom X, left Y, right Y2),
  // so we do NOT force domains to include crossAt=0 unless explicitly provided.
  const xCrossValues = (() => {
    if (hasY2Axis) {
      if (xAxisCrossAt2 === undefined) return [];
      return [xAxisCrossAt2];
    }

    if (xAxisCrossAt2 === undefined) return [xAxisCrossAt];
    return [xAxisCrossAt, xAxisCrossAt2];
  })();

  const xDomain = (() => {
    if (xCrossValues.length === 0) return [xMin, xMax];
    return [Math.min(xMin, ...xCrossValues), Math.max(xMax, ...xCrossValues)];
  })();

  const yDomain = hasY2Axis ? [yMin, yMax] : [Math.min(yMin, yAxisCrossAt), Math.max(yMax, yAxisCrossAt)];
  const y2Domain = [y2Min, y2Max];
  const xDomainMin = xDomain[0];
  const xDomainMax = xDomain[1];
  const yDomainMin = yDomain[0];
  const yDomainMax = yDomain[1];
  const y2DomainMin = y2Domain[0];
  const y2DomainMax = y2Domain[1];

  // Calculate final ranges from domains for tick step calculation
  const finalXRange = xDomainMax - xDomainMin || 1;
  const finalYRange = yDomainMax - yDomainMin || 1;
  const finalY2Range = y2DomainMax - y2DomainMin || 1;

  // SVG dimensions - calculate width based on container
  const [svgWidth, setSvgWidth] = useState(width ?? 800);
  const svgHeight = height ?? 400;
  const svgPadding = {top: 40, bottom: 50, left: 60, right: 40};

  useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current && !width) {
        const container = svgRef.current.parentElement;
        if (container) {
          setSvgWidth(container.clientWidth || 800);
        }
      }
    };

    if (!width) {
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, [width]);

  const plotWidth = svgWidth - svgPadding.left - svgPadding.right;
  const plotHeight = svgHeight - svgPadding.top - svgPadding.bottom;

  // Calculate tick marks for x axis
  const xTickStep = xAxis?.tickStep ?? (finalXRange <= 10 ? 1 : finalXRange <= 50 ? 5 : finalXRange <= 100 ? 10 : 20);
  const xTicks = useMemo(() => {
    const ticks: number[] = [];
    const start = Math.ceil(xDomainMin / xTickStep) * xTickStep;
    for (let i = start; i <= xDomainMax; i += xTickStep) {
      if (i >= xDomainMin && i <= xDomainMax) {
        ticks.push(i);
      }
    }
    return ticks;
  }, [xDomainMax, xDomainMin, xTickStep]);

  // Calculate tick marks for y axis
  const yTickStep = yAxis?.tickStep ?? (finalYRange <= 10 ? 1 : finalYRange <= 50 ? 5 : finalYRange <= 100 ? 10 : 20);
  const yTicks = useMemo(() => {
    const ticks: number[] = [];
    const start = Math.ceil(yDomainMin / yTickStep) * yTickStep;
    for (let i = start; i <= yDomainMax; i += yTickStep) {
      if (i >= yDomainMin && i <= yDomainMax) {
        ticks.push(i);
      }
    }
    return ticks;
  }, [yDomainMax, yDomainMin, yTickStep]);

  const y2TickStep =
    y2Axis?.tickStep ?? (finalY2Range <= 10 ? 1 : finalY2Range <= 50 ? 5 : finalY2Range <= 100 ? 10 : 20);
  const y2Ticks = useMemo(() => {
    const ticks: number[] = [];
    const start = Math.ceil(y2DomainMin / y2TickStep) * y2TickStep;
    for (let i = start; i <= y2DomainMax; i += y2TickStep) {
      if (i >= y2DomainMin && i <= y2DomainMax) {
        ticks.push(i);
      }
    }
    return ticks;
  }, [y2DomainMax, y2DomainMin, y2TickStep]);

  // Scale functions: convert data coordinates to SVG coordinates
  const scaleX = (value: number): number => {
    return svgPadding.left + ((value - xDomainMin) / (xDomainMax - xDomainMin)) * plotWidth;
  };

  const scaleY = (value: number): number => {
    // Y axis is inverted (SVG coordinates: top is 0, bottom is height)
    return svgPadding.top + plotHeight - ((value - yDomainMin) / (yDomainMax - yDomainMin)) * plotHeight;
  };

  const scaleY2 = (value: number): number => {
    // Y2 axis is inverted (SVG coordinates: top is 0, bottom is height)
    return svgPadding.top + plotHeight - ((value - y2DomainMin) / (y2DomainMax - y2DomainMin)) * plotHeight;
  };

  // Render point shape
  const renderPoint = (x: number, y: number, point: ScatterDataPoint, defaultColor: string) => {
    const pointSize = point.pointSize ?? 6;
    const pointColor = point.pointColor ? resolveColor(point.pointColor) : defaultColor;
    const shape = point.pointShape ?? 'circle';

    switch (shape) {
      case 'circle':
        return <circle cx={x} cy={y} r={pointSize} fill={pointColor} />;
      case 'square':
        return (
          <rect x={x - pointSize} y={y - pointSize} width={pointSize * 2} height={pointSize * 2} fill={pointColor} />
        );
      case 'triangle':
        return (
          <polygon
            points={`${x},${y - pointSize} ${x - pointSize},${y + pointSize} ${x + pointSize},${y + pointSize}`}
            fill={pointColor}
          />
        );
      case 'diamond':
        return (
          <polygon
            points={`${x},${y - pointSize} ${x + pointSize},${y} ${x},${y + pointSize} ${x - pointSize},${y}`}
            fill={pointColor}
          />
        );
      case 'cross':
        return (
          <g stroke={pointColor} strokeWidth={2}>
            <line x1={x - pointSize} y1={y - pointSize} x2={x + pointSize} y2={y + pointSize} />
            <line x1={x - pointSize} y1={y + pointSize} x2={x + pointSize} y2={y - pointSize} />
          </g>
        );
      default:
        return <circle cx={x} cy={y} r={pointSize} fill={pointColor} />;
    }
  };

  const textColor = theme.palette.text.primary;
  const gridColor = theme.palette.divider;
  const xAxisY = hasY2Axis ? svgPadding.top + plotHeight : scaleY(yAxisCrossAt);
  const yAxisX = hasY2Axis ? svgPadding.left : scaleX(xAxisCrossAt);
  const y2AxisX = hasY2Axis
    ? xAxisCrossAt2 !== undefined
      ? scaleX(xAxisCrossAt2)
      : svgPadding.left + plotWidth
    : undefined;

  return (
    <Box
      sx={{
        width: '100%',
        py: 2,
        px: 1,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{display: 'block'}}
      >
        {/* Grid lines */}
        {xAxis?.showGrid !== false &&
          xTicks.map((tick) => {
            const x = scaleX(tick);
            return (
              <line
                key={`x-grid-${tick}`}
                x1={x}
                y1={svgPadding.top}
                x2={x}
                y2={svgPadding.top + plotHeight}
                stroke={gridColor}
                strokeWidth={1}
                strokeDasharray="2,2"
                opacity={0.3}
              />
            );
          })}
        {yAxis?.showGrid !== false &&
          yTicks.map((tick) => {
            const y = scaleY(tick);
            return (
              <line
                key={`y-grid-${tick}`}
                x1={svgPadding.left}
                y1={y}
                x2={svgPadding.left + plotWidth}
                y2={y}
                stroke={gridColor}
                strokeWidth={1}
                strokeDasharray="2,2"
                opacity={0.3}
              />
            );
          })}

        {/* X axis */}
        <line
          x1={svgPadding.left}
          y1={xAxisY}
          x2={svgPadding.left + plotWidth}
          y2={xAxisY}
          stroke={textColor}
          strokeWidth={2}
        />

        {/* Y axis (left) */}
        <line
          x1={yAxisX}
          y1={svgPadding.top}
          x2={yAxisX}
          y2={svgPadding.top + plotHeight}
          stroke={textColor}
          strokeWidth={2}
        />

        {/* Y2 axis (right) */}
        {hasY2Axis && y2AxisX !== undefined && (
          <line
            x1={y2AxisX}
            y1={svgPadding.top}
            x2={y2AxisX}
            y2={svgPadding.top + plotHeight}
            stroke={textColor}
            strokeWidth={2}
          />
        )}

        {/* X axis ticks and labels */}
        {xAxis?.showLabels !== false &&
          xTicks.map((tick) => {
            const x = scaleX(tick);
            return (
              <g key={`x-tick-${tick}`}>
                <line x1={x} y1={xAxisY} x2={x} y2={xAxisY + 5} stroke={textColor} strokeWidth={1} />
                <text x={x} y={xAxisY + 20} textAnchor="middle" fontSize="12" fill={textColor}>
                  {tick}
                </text>
              </g>
            );
          })}

        {/* Y axis ticks and labels */}
        {yAxis?.showLabels !== false &&
          yTicks.map((tick) => {
            const y = scaleY(tick);
            return (
              <g key={`y-tick-${tick}`}>
                <line x1={yAxisX} y1={y} x2={yAxisX - 5} y2={y} stroke={textColor} strokeWidth={1} />
                <text x={yAxisX - 10} y={y + 4} textAnchor="end" fontSize="12" fill={textColor}>
                  {tick}
                </text>
              </g>
            );
          })}

        {/* Y2 axis ticks and labels */}
        {hasY2Axis &&
          y2Axis?.showLabels !== false &&
          y2AxisX !== undefined &&
          y2Ticks.map((tick) => {
            const y = scaleY2(tick);
            return (
              <g key={`y2-tick-${tick}`}>
                <line x1={y2AxisX} y1={y} x2={y2AxisX + 5} y2={y} stroke={textColor} strokeWidth={1} />
                <text x={y2AxisX + 10} y={y + 4} textAnchor="start" fontSize="12" fill={textColor}>
                  {tick}
                </text>
              </g>
            );
          })}

        {/* Axis titles */}
        {xAxis?.title && (
          <text
            x={svgPadding.left + plotWidth / 2}
            y={svgHeight - 5}
            textAnchor="middle"
            fontSize="14"
            fill={textColor}
            fontWeight="500"
          >
            {xAxis.title}
          </text>
        )}
        {yAxis?.title && (
          <text
            x={15}
            y={svgPadding.top + plotHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill={textColor}
            fontWeight="500"
            transform={`rotate(-90, 15, ${svgPadding.top + plotHeight / 2})`}
          >
            {yAxis.title}
          </text>
        )}
        {hasY2Axis && y2Axis?.title && (
          <text
            x={svgWidth - 15}
            y={svgPadding.top + plotHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill={textColor}
            fontWeight="500"
            transform={`rotate(90, ${svgWidth - 15}, ${svgPadding.top + plotHeight / 2})`}
          >
            {y2Axis.title}
          </text>
        )}

        {/* Lines connecting points */}
        {line &&
          dataSets.length > 0 &&
          (() => {
            const strokeWidth = line.strokeWidth ?? 2;
            const strokeDasharray = line.strokeDasharray;

            return (
              <g>
                {dataSets.flatMap((ds, seriesIndex) => {
                  if (ds.data.length <= 1) return [];

                  const defaultColor = resolveColor(
                    seriesPalette[seriesIndex % seriesPalette.length] || theme.palette.primary.main,
                  );
                  const lineColor = line.color ? resolveColor(line.color) : defaultColor;
                  const scaleSeriesY = ds.isY2Axis ? scaleY2 : scaleY;

                  return ds.data.slice(0, -1).map((point, index) => {
                    const x1 = scaleX(point.x);
                    const y1 = scaleSeriesY(point.y);
                    const nextPoint = ds.data[index + 1];
                    const x2 = scaleX(nextPoint.x);
                    const y2 = scaleSeriesY(nextPoint.y);
                    const lineKey = `line-${seriesIndex}-${point.x}|${point.y}|${nextPoint.x}|${nextPoint.y}`;

                    return (
                      <line
                        key={lineKey}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={lineColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={strokeDasharray}
                        fill="none"
                      />
                    );
                  });
                })}
              </g>
            );
          })()}

        {/* Equation lines */}
        {equationLines &&
          equationLines.length > 0 &&
          (() => {
            return (
              <g>
                {equationLines.map((eqLine) => {
                  const color = eqLine.color
                    ? resolveColor(eqLine.color)
                    : theme.palette.primary.dark || theme.palette.primary.main;
                  const strokeWidth = eqLine.strokeWidth ?? 2;
                  const strokeDasharray = eqLine.strokeDasharray;
                  const eqLineKey = [
                    'eq',
                    eqLine.equationType ?? 'function',
                    eqLine.equation ?? '',
                    eqLine.centerX ?? '',
                    eqLine.centerY ?? '',
                    eqLine.radius ?? '',
                    eqLine.label ?? '',
                    eqLine.color ?? '',
                    eqLine.strokeWidth ?? '',
                    eqLine.strokeDasharray ?? '',
                  ].join('|');

                  // Points were already generated in overlays; filter by index is hard, so regenerate minimal path here based on the same helper.
                  // Keep it simple by generating a path from all equation points when only one line is expected; for multiple lines we accept
                  // slight redundancy in computation (small arrays).
                  const points = collectOverlayPoints({
                    data: allPoints,
                    equationLines: [eqLine],
                    svgPaths: [],
                    context: {xAxis, yAxis},
                  }).equationPoints;

                  const d = buildSvgPathDFromPoints(points, scaleX, scaleY);
                  if (!d) return null;

                  const labelPoint = points.reduce<{x: number; y: number} | null>((acc, p) => {
                    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return acc;
                    return {x: p.x, y: p.y};
                  }, null);

                  const labelX = labelPoint ? scaleX(labelPoint.x) : undefined;
                  const labelY = labelPoint ? scaleY(labelPoint.y) - 8 : undefined;

                  return (
                    <g key={eqLineKey}>
                      <path
                        d={d}
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeDasharray={strokeDasharray}
                        fill="none"
                      />
                      {eqLine.label && labelX !== undefined && labelY !== undefined && (
                        <text
                          x={labelX}
                          y={labelY}
                          textAnchor="middle"
                          fontSize={eqLine.labelFontSize ?? '11'}
                          fill={eqLine.labelFontColor ? resolveColor(eqLine.labelFontColor) : color}
                        >
                          {eqLine.label}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })()}

        {/* SVG path overlays (plot-pixel coordinates) */}
        {overlays.svgPaths.length > 0 && (
          <g transform={`translate(${svgPadding.left}, ${svgPadding.top})`}>
            {overlays.svgPaths.map((p) => {
              const stroke = p.color ? resolveColor(p.color) : theme.palette.primary.dark || theme.palette.primary.main;
              const strokeWidth = p.strokeWidth ?? 2;
              const strokeDasharray = p.strokeDasharray;
              const fill = p.fill ? resolveColor(p.fill) : 'none';
              const svgPathKey = ['svg-path', p.svgPath, p.label ?? '', p.color ?? '', p.strokeWidth ?? ''].join('|');

              const labelAnchor = p.label ? getFirstMoveToPoint(p.svgPath) : null;

              return (
                <g key={svgPathKey}>
                  <path
                    d={p.svgPath}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray}
                    fill={fill}
                  />
                  {p.label && labelAnchor && (
                    <text
                      x={labelAnchor.x}
                      y={labelAnchor.y - 6}
                      textAnchor="start"
                      fontSize={p.labelFontSize ?? '11'}
                      fill={p.labelFontColor ? resolveColor(p.labelFontColor) : stroke}
                    >
                      {p.label}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        )}

        {/* Data points */}
        {dataSets.map((ds, seriesIndex) => {
          const defaultColor = resolveColor(
            seriesPalette[seriesIndex % seriesPalette.length] || theme.palette.primary.main,
          );
          const seriesKey = `series-${seriesIndex}-${ds.label ?? ''}`;
          const scaleSeriesY = ds.isY2Axis ? scaleY2 : scaleY;

          return (
            <g key={seriesKey}>
              {ds.data.map((point) => {
                const x = scaleX(point.x);
                const y = scaleSeriesY(point.y);
                const pointKey = [
                  'point',
                  seriesIndex,
                  point.x,
                  point.y,
                  point.label ?? '',
                  point.pointShape ?? 'circle',
                  point.pointColor ?? '',
                  point.pointSize ?? '',
                ].join('|');
                return (
                  <g key={pointKey}>
                    {renderPoint(x, y, point, defaultColor)}
                    {point.label && (
                      <text
                        x={x}
                        y={y - (point.pointSize ?? 6) - 5}
                        textAnchor="middle"
                        fontSize={point.labelFontSize ?? '11'}
                        fill={point.labelFontColor ? resolveColor(point.labelFontColor) : textColor}
                      >
                        {point.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Legend */}
        {(() => {
          const legendEntries = dataSets
            .map((ds, seriesIndex) => {
              const label = ds.label?.trim();
              if (!label) return null;

              const defaultColor = resolveColor(
                seriesPalette[seriesIndex % seriesPalette.length] || theme.palette.primary.main,
              );
              const firstPointColor = ds.data.find((p) => Boolean(p.pointColor))?.pointColor;
              const color = firstPointColor ? resolveColor(firstPointColor) : defaultColor;

              return {label, color};
            })
            .filter((v): v is {label: string; color: string} => Boolean(v));

          const shouldShowLegend = Boolean(legend?.enabled) && legendEntries.length > 0;
          if (!shouldShowLegend) return null;

          const position = legend?.position ?? 'top';
          const itemHeight = 16;
          const boxSize = 10;
          const padding = 6;

          const legendX =
            position === 'left'
              ? svgPadding.left
              : position === 'right'
                ? svgWidth - svgPadding.right - 140
                : svgPadding.left;

          const legendY =
            position === 'top'
              ? padding
              : position === 'bottom'
                ? svgHeight - svgPadding.bottom + padding
                : svgPadding.top;

          return (
            <g>
              <g transform={`translate(${legendX}, ${legendY})`}>
                {legendEntries.map((entry, index) => {
                  const y = index * itemHeight;
                  return (
                    <g key={`legend-${entry.label}-${entry.color}`} transform={`translate(0, ${y})`}>
                      <rect x={0} y={-boxSize + 2} width={boxSize} height={boxSize} fill={entry.color} rx={2} />
                      <text x={boxSize + 6} y={0} fontSize="12" fill={textColor} dominantBaseline="middle">
                        {entry.label}
                      </text>
                    </g>
                  );
                })}
              </g>
            </g>
          );
        })()}
      </svg>
    </Box>
  );
}
